import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ImageOff, X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, GALLERY_MIN_SLOTS } from "../../data/content";

const PAGE_SIZE = 8; // 2 filas x 4 columnas

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(0);

  // Lista combinada: fotos reales + huecos "Próximamente" hasta el mínimo.
  const tiles = useMemo(() => {
    const imgs = GALLERY.map((img, i) => ({ type: "img", img, i }));
    const placeholders = Math.max(0, GALLERY_MIN_SLOTS - GALLERY.length);
    const phs = Array.from({ length: placeholders }).map((_, i) => ({ type: "ph", i }));
    return [...imgs, ...phs];
  }, []);

  const totalPages = Math.ceil(tiles.length / PAGE_SIZE);
  const start = page * PAGE_SIZE;
  const current = tiles.slice(start, start + PAGE_SIZE);

  const goTo = (p) => {
    const next = Math.max(0, Math.min(totalPages - 1, p));
    setPage(next);
    const el = document.querySelector("#mis-trabajos");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="mis-trabajos" data-testid="gallery-section" className="max-w-[1400px] mx-auto px-5 md:px-12 pt-16 md:pt-20 pb-24 md:pb-32">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-16 items-end">
        <div className="md:col-span-9">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5AB82D]">
            Galería
          </span>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-[#0A1F10] leading-[1.05]">
            Mis trabajos
          </h2>
          <p className="mt-5 max-w-xl text-base text-[#4B5E51] leading-relaxed">
            Una selección de instalaciones y reparaciones reales realizadas en Huelva y alrededores.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {current.map((tile) =>
          tile.type === "img" ? (
            <motion.button
              key={`img-${tile.i}`}
              type="button"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              onClick={() => setActive(tile.img)}
              data-testid={`gallery-item-${tile.i}`}
              className="group relative overflow-hidden rounded-sm aspect-[5/4] bg-[#EBF6E4] focus:outline-none focus:ring-2 focus:ring-[#5AB82D]"
            >
              <img
                src={tile.img.src}
                alt={tile.img.alt || `Trabajo de fontanería ${tile.i + 1}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <span className="absolute inset-0 bg-[#0A1F10]/0 group-hover:bg-[#0A1F10]/20 transition-colors duration-300" />
            </motion.button>
          ) : (
            <motion.div
              key={`ph-${start}-${tile.i}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid={`gallery-placeholder-${tile.i}`}
              className="flex flex-col items-center justify-center gap-2 rounded-sm aspect-[5/4] bg-[#EBF6E4] border border-dashed border-[#5AB82D]/30 text-[#5AB82D]/60"
            >
              <ImageOff className="w-6 h-6" strokeWidth={1.5} />
              <span className="text-[10px] font-semibold uppercase tracking-wide">Próximamente</span>
            </motion.div>
          )
        )}
      </div>

      {totalPages > 1 && (
        <div data-testid="gallery-pagination" className="mt-12 flex items-center justify-center gap-2">
          <button
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            data-testid="gallery-prev"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#0A1F10]/15 text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {Array.from({ length: totalPages }).map((_, p) => (
            <button
              key={p}
              onClick={() => goTo(p)}
              data-testid={`gallery-page-${p}`}
              className={`w-11 h-11 rounded-full text-sm font-bold transition-colors ${
                p === page
                  ? "bg-[#5AB82D] text-white"
                  : "border border-[#0A1F10]/15 text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D]"
              }`}
            >
              {p + 1}
            </button>
          ))}

          <button
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages - 1}
            data-testid="gallery-next"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#0A1F10]/15 text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-[#0A1F10]/90 backdrop-blur-sm p-5"
          >
            <button
              onClick={() => setActive(null)}
              data-testid="gallery-lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3 }}
              src={active.src}
              alt={active.alt || "Trabajo de fontanería"}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
